import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Eye, EyeOff, Pencil, Plus, SquareStack, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import {
  DataTable,
  useDataTable,
  useSearchListState,
  type ListParams,
  type RowAction,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { ContentBlock, ContentBlockStatus } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { useStore } from '@/shared/store/store-context';

import { useBlockColumns } from '../components/block-columns';
import { blockFilters } from '../components/block-filters';
import { DeleteBlockDialog } from '../components/delete-block-dialog';
import { useUpdateBlock } from '../hooks/use-block-mutations';
import { useBlocks } from '../hooks/use-blocks';
import { blockApiParams } from '../schemas/list-search';

const STORE_ROUTE_ID = '/_authed/_active/s/$storeId/content/blocks/' as const;
const STORE_ROUTE_PATH = '/s/$storeId/content/blocks/' as const;
const ORG_ROUTE_ID = '/_authed/_active/org/shared-blocks/' as const;
const ORG_ROUTE_PATH = '/org/shared-blocks/' as const;

function NewBlockButton({ storeId, tour = false }: { storeId: string | null; tour?: boolean }) {
  const { t } = useTranslation('content');
  const tourTarget = storeId === null ? 'shared-blocks.list.new' : 'content.blocks.new';
  return (
    <Can permission="content.write">
      <Button asChild data-tour={tour ? tourTarget : undefined}>
        {storeId === null ? (
          <Link to="/org/shared-blocks/new">
            <Plus aria-hidden />
            {t('blocks.newShared')}
          </Link>
        ) : (
          <Link to="/s/$storeId/content/blocks/new" params={{ storeId }}>
            <Plus aria-hidden />
            {t('blocks.new')}
          </Link>
        )}
      </Button>
    </Can>
  );
}

interface BlocksListProps {
  storeId: string | null;
  storeName?: string;
  search: Readonly<Record<string, unknown>>;
  onSearchChange: (patch: SearchPatch) => void;
  /** The list's params as the endpoint takes them. */
  toApiParams: (params: ListParams) => ListParams;
  openRow: (row: ContentBlock) => void;
}

function BlocksList({
  storeId,
  storeName,
  search,
  onSearchChange,
  toApiParams,
  openRow,
}: BlocksListProps) {
  const { t } = useTranslation('content');
  const message = useErrorMessage();
  const { can } = usePermissions();
  const canWrite = can('content.write');
  const update = useUpdateBlock(storeId);
  const [deleting, setDeleting] = useState<ContentBlock | null>(null);

  const filters = useMemo(() => blockFilters(t, { withScope: storeId !== null }), [storeId, t]);
  const list = useSearchListState({ search, onSearchChange, filters });
  const params = useMemo(() => toApiParams(list.params), [list.params, toApiParams]);
  const query = useBlocks(storeId, params);

  const mutateStatus = update.mutate;
  const setStatus = useCallback(
    (row: ContentBlock, status: ContentBlockStatus) =>
      mutateStatus(
        { id: row.id, body: { status } },
        { onError: (error) => toast.error(message(error)) },
      ),
    [message, mutateStatus],
  );

  // A shared block listed inside a store is edited under the organisation, so a store only opens it.
  const readOnly = useCallback(
    (row: ContentBlock) => !canWrite || (storeId !== null && row.scope === 'shared'),
    [canWrite, storeId],
  );

  const rowActions = useMemo<Array<RowAction<ContentBlock>>>(
    () => [
      { id: 'open', label: t('actions.open'), icon: <Pencil aria-hidden />, onAction: openRow },
      {
        id: 'draft',
        label: t('actions.setDraft'),
        icon: <EyeOff aria-hidden />,
        separatorBefore: true,
        hidden: (row) => readOnly(row) || row.status !== 'active',
        onAction: (row) => setStatus(row, 'draft'),
      },
      {
        id: 'activate',
        label: t('actions.activate'),
        icon: <Eye aria-hidden />,
        separatorBefore: true,
        hidden: (row) => readOnly(row) || row.status === 'active',
        onAction: (row) => setStatus(row, 'active'),
      },
      {
        id: 'delete',
        label: t('actions.delete'),
        icon: <Trash2 aria-hidden />,
        variant: 'destructive',
        separatorBefore: true,
        hidden: readOnly,
        onAction: (row) => setDeleting(row),
      },
    ],
    [openRow, readOnly, setStatus, t],
  );

  const columns = useBlockColumns(storeId);
  const rows = query.data?.items ?? [];
  const table = useDataTable({
    mode: 'server',
    data: rows,
    nextCursor: query.data?.nextCursor,
    list,
    columns,
    filters,
    getRowId: (row) => row.id,
    tableId: storeId === null ? 'shared-blocks' : 'blocks',
  });

  const narrowed = Boolean(list.q) || Object.keys(list.filters).length > 0 || Boolean(list.cursor);
  const isEmptyList = query.isSuccess && rows.length === 0 && !narrowed;

  return (
    <PageContent>
      <PageHeader
        title={storeId === null ? t('blocks.sharedTitle') : t('blocks.title')}
        description={
          storeId === null
            ? t('blocks.sharedDescription')
            : t('blocks.description', { store: storeName ?? '' })
        }
        actions={<NewBlockButton storeId={storeId} tour />}
      />
      {isEmptyList ? (
        <EmptyState
          icon={SquareStack}
          title={t('blocks.empty.title')}
          description={t('blocks.empty.description')}
          action={<NewBlockButton storeId={storeId} />}
          tour={storeId === null ? 'shared-blocks.list.empty' : 'content.blocks.empty'}
          tourLink
        />
      ) : (
        <DataTable
          table={table}
          tour={{ table: storeId === null ? 'shared-blocks.list.table' : 'content.blocks.table' }}
          search={{ placeholder: t('blocks.search'), label: t('blocks.search') }}
          loading={query.isPending}
          fetching={query.isFetching || update.isPending}
          error={query.error}
          onRetry={() => void query.refetch()}
          rowActions={rowActions}
          onRowClick={openRow}
          getRowLabel={(row) => row.name}
          resourceName={{ singular: t('blocks.resource.one'), plural: t('blocks.resource.other') }}
          emptyState={{ title: t('blocks.noMatches') }}
        />
      )}
      <DeleteBlockDialog
        storeId={storeId}
        row={deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      />
    </PageContent>
  );
}

const sharedApiParams = (params: ListParams) => params;

function StoreBlocks() {
  const store = useStore();
  const search = useSearch({ from: STORE_ROUTE_ID });
  const navigate = useNavigate({ from: STORE_ROUTE_PATH });
  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const openRow = useCallback(
    (row: ContentBlock) =>
      void (row.scope === 'shared'
        ? navigate({ to: '/org/shared-blocks/$blockId', params: { blockId: row.id } })
        : navigate({
            to: '/s/$storeId/content/blocks/$blockId',
            params: { storeId: store.id, blockId: row.id },
          })),
    [navigate, store.id],
  );
  return (
    <BlocksList
      storeId={store.id}
      storeName={store.name}
      search={search}
      onSearchChange={onSearchChange}
      toApiParams={blockApiParams}
      openRow={openRow}
    />
  );
}

function SharedBlocks() {
  const search = useSearch({ from: ORG_ROUTE_ID });
  const navigate = useNavigate({ from: ORG_ROUTE_PATH });
  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const openRow = useCallback(
    (row: ContentBlock) =>
      void navigate({ to: '/org/shared-blocks/$blockId', params: { blockId: row.id } }),
    [navigate],
  );
  return (
    <BlocksList
      storeId={null}
      search={search}
      onSearchChange={onSearchChange}
      toApiParams={sharedApiParams}
      openRow={openRow}
    />
  );
}

export function BlocksPage() {
  return (
    <FeatureErrorBoundary name="content.blocks">
      <StoreBlocks />
    </FeatureErrorBoundary>
  );
}

export function SharedBlocksPage() {
  return (
    <FeatureErrorBoundary name="content.sharedBlocks">
      <SharedBlocks />
    </FeatureErrorBoundary>
  );
}
